import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  ChartPieIcon, 
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ReceiptRefundIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

// Import components when they are created
import BenefitsOverview from '../../components/benefits/BenefitsOverview';
import CoachesList from '../../components/benefits/CoachesList';
import RevenueAnalysis from '../../components/benefits/RevenueAnalysis';
import ExpenseTracker from '../../components/benefits/ExpenseTracker';

const BenefitsPage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddForm, setShowAddForm] = useState(false);

  const tabs = [
    {
      id: 'overview',
      name: 'Aperçu des bénéfices',
      icon: ChartPieIcon,
      component: BenefitsOverview,
      description: 'Vue générale des bénéfices et revenus'
    },
    {
      id: 'coaches',
      name: 'Coachs',
      icon: AcademicCapIcon,
      component: CoachesList,
      description: 'Gestion des coachs et leurs performances'
    },
    {
      id: 'revenue',
      name: 'Analyse de revenus',
      icon: ArrowTrendingUpIcon,
      component: RevenueAnalysis,
      description: 'Analyse détaillée des sources de revenus'
    },
    {
      id: 'expenses',
      name: 'Suivi des dépenses',
      icon: BanknotesIcon,
      component: ExpenseTracker,
      description: 'Gestion et suivi des dépenses'
    }
  ];

  const CurrentTabComponent = tabs.find(tab => tab.id === activeTab)?.component || (() => <div>Contenu en développement</div>);

  // Statistiques des bénéfices
  const stats = [
    {
      title: 'Revenus Totaux',
      value: '4 750 000 DA',
      icon: BanknotesIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Dépenses Totales',
      value: '2 850 000 DA',
      icon: CurrencyDollarIcon,
      color: 'from-red-500 to-red-600',
    },
    {
      title: 'Bénéfice Net',
      value: '1 900 000 DA',
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Marge Bénéficiaire',
      value: '40%',
      icon: ArrowTrendingUpIcon,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">
            Bénéfices
          </h1>
          <p className="text-white/60 mt-2">
            Gestion des bénéfices et revenus
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Ajouter</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <CurrentTabComponent showAddForm={showAddForm} setShowAddForm={setShowAddForm} />
      </motion.div>
    </div>
  );
};

export default BenefitsPage; 